import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n/config';

export type NotificationOptions = {
  title?: string;
  body?: string;
  icon?: string;
  badge?: string;
  tag?: string;
  onClick?: () => void;
  onClose?: () => void;
  onError?: (event: Event) => void;
};

const useSystemNotification = () => {
  // 检查是否支持通知
  const isSupported = typeof window !== 'undefined' && 'Notification' in window;

  // 发送通知的函数
  const sendNotification = useCallback(
    async (options: NotificationOptions = {}) => {
      try {
        // 检查浏览器是否支持通知
        if (!('Notification' in window)) {
          console.warn('浏览器不支持通知功能');
          return null;
        }

        // 再次检查权限，确保权限状态一致
        if (Notification.permission !== 'granted') {
          console.error('通知权限被拒绝，无法发送通知');
          return null;
        }

        // 创建通知对象
        const notification = new Notification(
          options.title || i18n.t('common:notification.title'),
          {
            body: options.body || i18n.t('common:notification.body'),
            icon: options.icon || '/favicon.ico',
            badge: options.badge || '/favicon.ico',
            tag: options.tag,
          },
        );

        // 点击通知时的行为
        notification.onclick = () => {
          window.focus();
          notification.close();
          options.onClick?.();
        };

        // 通知关闭时的行为
        notification.onclose = () => {
          options.onClose?.();
        };

        // 通知出错时的行为
        notification.onerror = (event) => {
          console.error('发送通知错误', event);
          options.onError?.(event);
        };

        return notification;
      } catch (error) {
        console.error('发送通知错误', error);
        return null;
      }
    },
    [],
  );

  return {
    isSupported,
    sendNotification,
  };
};

export default useSystemNotification;
